import { useEffect, useMemo, useRef, useState } from 'react'
import * as d3 from 'd3'
import { useAuth0 } from '@auth0/auth0-react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import pick from 'lodash/pick'
import EllipsisCell from '../../../components/DataTable/EllipsisCell.component'
import { formatDate } from '../../../utils/formatting'
import { DATE_COLUMN_WIDTH, ID_COLUMN_WIDTH } from '../../../components/DataTable/DataTable.constants'

import { AppLayoutContext } from '../../../providers/AppLayout'
import { useUrlStateParams } from '../../../utils/url'
import { APP_UPDATE_NOTICE, SnackbarContext } from '../../../providers/SnackbarContext'

import { AppPermissions } from '../../../models/AppPermissions.model'

import UserService from '../../../services/UserService/UserService'
import { ObstructionReportsService } from '../../../services/ObstructionReportsService/ObstructionReportsService'

import {
  Box,
  FormControl,
  OutlinedInput,
  InputLabel,
  MenuItem,
  Stack,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  Paper,
  Menu,
  FormGroup,
  FormControlLabel,
  Switch,
} from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import CloseIcon from '@mui/icons-material/Close'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import DataTable from '../../../components/DataTable/DataTable.component'
import Loading from '../../../components/Loading/Loading.component'
import ImportExportDialog, {
  ImportExportDialogProps,
} from '../../../components/ImportExportDialog/ImportExportDialog.component'
import Link from '@mui/material/Link'
import { AppStateContext } from '../../../providers/AppStateContext'
import { ObstructionReportsRelations } from '../../../models/ObstructionReportsRelations.model'
import { ObstructionReport, obstructionReportFields } from '../../../models/ObstructionReport.model'
import ConfirmDeleteDialog from '../../../components/DeleteDialog/ConfirmDeleteDialog.component'

type ObstructionReportsSearchProps = {
  nested?: boolean
  systemId?: number
  trailId?: number
  hideHeader?: boolean
  excludeColumns?: string[]
}
const ObstructionReportsSearch = (props: ObstructionReportsSearchProps): JSX.Element => {
  const [currentSearchParameters] = useSearchParams()
  const {
    nested,
    systemId = Number(currentSearchParameters.get('trail-system')) || undefined,
    trailId = Number(currentSearchParameters.get('trail')) || undefined,
    hideHeader = !systemId && !trailId,
    excludeColumns = [],
  } = props

  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const { user } = useAuth0()
  const hasWritePermissions = UserService.hasPermissions(user, [AppPermissions.editObstructionReport])

  const [searchError, setSearchError] = useState('')
  const [rowActionsAnchorEl, setRowActionsAnchorEl] = useState<null | HTMLElement>(null)
  const isActionsMenuOpen = Boolean(rowActionsAnchorEl)
  const [selectedRow, setSelectedRow] = useState<ObstructionReport | undefined>()
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)
  const [isExpired, setIsExpired] = useState(false)

  const [reporterEmail, setReporterEmail] = useUrlStateParams(
    '',
    'reporterEmail',
    (value) => encodeURIComponent(value.toString()),
    (value) => (value ? decodeURIComponent(value) : ''),
  )
  const nameInputRef = useRef<HTMLInputElement>()
  const [orderBy, setOrderBy] = useState<keyof ObstructionReport | undefined>('id')
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc' | undefined>('desc')
  const [page, setPage] = useUrlStateParams(
    1,
    'page',
    (value) => value.toString(),
    (value) => (!isNaN(Number(value)) ? Number(value) : 1),
  )
  const [pageSize, setPageSize] = useUrlStateParams(
    12,
    'page-size',
    (value) => value.toString(),
    (value) => (!isNaN(Number(value)) ? Number(value) : 12),
  )

  const [isImportExportOpen, setIsImportExportOpen] = useState(false)
  const currentRegionCode = AppStateContext.getRegion()

  useEffect(() => {
    if (!nested) {
      AppLayoutContext.setPageName('Obstruction Reports')
    }
  }, [])

  const queryClient = useQueryClient()

  const obstructionReportsService = useMemo(() => new ObstructionReportsService(), []) // memo to prevent re-creating service on every render
  const { isLoading: isLoadingObstructionReports, data: obstructionReportsResponse } = useQuery(
    [
      'obstructionReportsService.search',
      {
        systemId,
        trailId,
        page,
        pageSize,
        reporterEmail: reporterEmail === '' ? undefined : reporterEmail,
        orderBy,
        orderDirection,
        isExpired,
        relations: [ObstructionReportsRelations.trail],
      },
      AppStateContext.getRegion(),
    ],
    async () => {
      return obstructionReportsService.search({
        systemId,
        trailId,
        page,
        pageSize,
        reporterEmail: reporterEmail === '' ? undefined : reporterEmail,
        orderBy,
        orderDirection,
        isExpired: isExpired,
        relations: [ObstructionReportsRelations.trail],
      })
    },
    {
      onError: (err) => {
        setSearchError(typeof err === 'string' ? err : 'An error occurred while searching for obstruction reports.')
        console.error(err)
      },
    },
  )
  useEffect(() => {
    if (
      obstructionReportsResponse &&
      page > 1 &&
      (!obstructionReportsResponse.total || (page - 1) * pageSize >= obstructionReportsResponse.total)
    ) {
      // page out of range was requested so reset to page 1
      setPage(1)
    }
  }, [obstructionReportsResponse])

  // consolidate the various loading states into one
  useEffect(() => {
    setIsLoading(isLoadingObstructionReports)
  }, [isLoadingObstructionReports])

  const handleRowActionsClick = (event: React.MouseEvent<HTMLButtonElement>, obstructionReport: ObstructionReport) => {
    setRowActionsAnchorEl(event.currentTarget)
    setSelectedRow(obstructionReport)
  }
  const handleRowActionsClose = () => {
    setRowActionsAnchorEl(null)
    setSelectedRow(undefined)
  }

  const columns = useMemo(() => {
    const columns: GridColDef<ObstructionReport>[] = [
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        renderCell: (params) => (
          <>
            <IconButton
              id={`row-actions-button-${params.row.id}`}
              color="primary"
              aria-label="row actions"
              aria-controls={selectedRow?.id === params.row.id ? 'row-actions-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={selectedRow?.id === params.row.id ? 'true' : undefined}
              onClick={(event) => handleRowActionsClick(event, params.row)}
            >
              <MoreVertIcon />
            </IconButton>
          </>
        ),
      },
      {
        field: 'id',
        headerName: 'ID',
        width: ID_COLUMN_WIDTH,
        renderCell: (params) => (
          <Link color="primary" aria-label="obstruction report detail" href={`/obstruction-reports/${params.row.id}`}>
            {params.row.id}
          </Link>
        ),
      },
      {
        field: 'obstructionType',
        headerName: 'Obstruction Type',
        flex: 1,
        minWidth: 150,
        maxWidth: 300,
        renderCell: (params) => <EllipsisCell>{params.row.obstructionType?.name || ''}</EllipsisCell>,
      },
      {
        field: 'trailName',
        headerName: 'Trail Name',
        flex: 1,
        minWidth: 110,
        maxWidth: 300,
        // not a report column, so the API can't sort by it
        sortable: false,
        renderCell: (params) => <EllipsisCell>{params.row.trail?.name || '-'}</EllipsisCell>,
      },
      {
        field: 'system',
        headerName: 'System Name',
        flex: 1,
        minWidth: 130,
        maxWidth: 300,
        sortable: false,
        renderCell: (params) => (
          <EllipsisCell>{params.row.trail?.system?.name || params.row.system?.name || '-'}</EllipsisCell>
        ),
      },
      {
        field: 'alertLevel',
        headerName: 'Alert Level',
        width: 120,
        renderCell: (params) => <span style={{ textTransform: 'capitalize' }}>{params.row.alertLevel}</span>,
      },
      {
        field: 'reportedAt',
        headerName: 'Reported At',
        width: DATE_COLUMN_WIDTH,
        renderCell: (params) => (
          <EllipsisCell>{params.row.reportedAt ? formatDate(params.row.reportedAt) : 'Never'}</EllipsisCell>
        ),
      },
    ]
    // remove any columns specified to be removed in the excludeColumns prop
    if (excludeColumns) {
      return columns.filter((column) => !excludeColumns.includes(column.field as string))
    }
    return columns
  }, [excludeColumns])

  const handleSubmit = () => {
    setPage(1)
    setReporterEmail(nameInputRef?.current?.value || '')
  }

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false)
    handleRowActionsClose()
  }

  const deleteObstructionReport = useMutation(
    (mutationParams: { id: number }) => {
      setIsSaving(true)
      return obstructionReportsService.delete(mutationParams.id)
    },
    {
      onSuccess: () => {
        SnackbarContext.show('Obstruction report archived successfully!', 'success', APP_UPDATE_NOTICE)
        setRowActionsAnchorEl(null)
        setSelectedRow(undefined)
        // invalidate all queries to ensure any queries that are using the modified record are updated
        queryClient.invalidateQueries()
        setIsSaving(false)
      },
      onError: (err) => {
        SnackbarContext.show(`Obstruction report failed to archive: ${err}`, 'error')
        console.error(err)
        setIsSaving(false)
      },
    },
  )

  const handleDelete = () => {
    if (selectedRow?.id) {
      setIsDeleteConfirmationOpen(false)
      deleteObstructionReport.mutate({ id: selectedRow.id })
    }
  }

  const handleImportExportClose = () => {
    setIsImportExportOpen(false)
  }

  const handleImport: ImportExportDialogProps['onImport'] = (data: Partial<ObstructionReport>[], options) => {
    setIsImportExportOpen(false)
    if (!data) {
      SnackbarContext.show('Failed to read/parse file', 'error')
      console.error('Failed to read/parse file')
    } else {
      setIsSaving(true)
      obstructionReportsService
        .import(data, options)
        .then(() => {
          SnackbarContext.show('Obstruction reports imported successfully!', 'success', APP_UPDATE_NOTICE)

          // fetch new data
          setSearchError('')
          // invalidate all queries to ensure any queries that are using the modified record are updated
          queryClient.invalidateQueries()
          setIsSaving(false)
        })
        .catch((err) => {
          SnackbarContext.show(`Obstruction reports failed to import: ${err}`, 'error')
          console.error(err)
          setIsSaving(false)
        })
    }
  }

  const handleExport = () => {
    // do a request to the API to get the data to export with no pagination
    setIsSaving(true)
    setIsImportExportOpen(false)
    obstructionReportsService
      .search({
        trailId,
        pageSize: 0,
        reporterEmail,
        orderBy,
        orderDirection,
        relations: [ObstructionReportsRelations.trail],
      })
      .then((results) => {
        const exportData = results.data.map((obstructionReport) => {
          const obstructionReportToExport: Record<string, unknown> = { ...obstructionReport }
          obstructionReportToExport.trailId = obstructionReport.trail?.id
          obstructionReportToExport.trailName = obstructionReport.trail?.name
          delete obstructionReportToExport.system
          delete obstructionReportToExport.trail

          obstructionReportToExport.start =
            obstructionReportToExport.start instanceof Date ? obstructionReportToExport.start.toISOString() : ''
          obstructionReportToExport.end =
            obstructionReportToExport.end instanceof Date ? obstructionReportToExport.end.toISOString() : ''
          obstructionReportToExport.updatedAt =
            obstructionReportToExport.updatedAt instanceof Date ? obstructionReportToExport.updatedAt.toISOString() : ''
          obstructionReportToExport.createdAt =
            obstructionReportToExport.createdAt instanceof Date ? obstructionReportToExport.createdAt.toISOString() : ''

          // return the object with the props in the order we want them to appear in the csv
          const propsToExport: string[] = [...obstructionReportFields]
          // add system and trail props after id
          propsToExport.splice(propsToExport.indexOf('region') + 1, 0, 'systemId', 'systemName', 'trailId', 'trailName')
          return pick(obstructionReportToExport, propsToExport)
        })
        const csvData = d3.csvFormat(exportData)
        // initiate a download of the csv file
        const blob = new Blob([csvData], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.setAttribute('href', url)
        link.setAttribute('download', 'obstructionReports.csv')
        link.click()
        setIsSaving(false)
      })
      .catch((err) => {
        SnackbarContext.show(`Failed to fetch obstruction reports for export: ${err}`, 'error')
        console.error(err)
      })
  }

  return (
    <>
      <ConfirmDeleteDialog
        open={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onDelete={handleDelete}
        title="Confirm Archive"
        content={`Are you sure you want to archive obstruction report id ${selectedRow?.id}: ${selectedRow?.obstructionType}?`}
      />
      <ImportExportDialog
        open={isImportExportOpen}
        disableImport={!hasWritePermissions}
        exampleImportFileUrl="/example-import-files/obstructionReports.csv"
        importFields={[
          ...obstructionReportFields.filter((field) => !['system', 'trail', 'updatedAt', 'createdAt'].includes(field)),
          'systemId',
          'trailId',
        ]}
        onImport={handleImport}
        onExport={handleExport}
        onClose={handleImportExportClose}
      />
      <Paper sx={{ p: 2 }}>
        {!hideHeader && (
          <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
            Obstruction Reports
          </Typography>
        )}
        {(isLoading || isSaving) && !searchError && <Loading sx={{ py: 20 }} />}
        {searchError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{searchError}</Typography>
          </Stack>
        )}
        {!isLoading && !isSaving && !searchError && (
          <>
            <Box sx={{ pt: 1, mb: 1 }}>
              <Stack direction={{ zero: 'column', md: 'row' }} spacing={1}>
                <FormControl variant="outlined" sx={{ width: '100%' }}>
                  <InputLabel>Reporter Email</InputLabel>
                  <OutlinedInput
                    id="search-email"
                    label="Email"
                    defaultValue={reporterEmail || ''}
                    inputRef={nameInputRef}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSubmit()
                      }
                    }}
                    endAdornment={
                      nameInputRef?.current?.value ? (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="clear search name"
                            onClick={() => {
                              if (nameInputRef?.current?.value) {
                                nameInputRef.current.value = ''
                              }
                              setPage(1)
                              setReporterEmail('')
                            }}
                            edge="end"
                          >
                            <CloseIcon />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined
                    }
                  />
                </FormControl>

                <Button
                  variant="outlined"
                  onClick={handleSubmit}
                  sx={(theme) => ({ [theme.breakpoints.up('md')]: { width: 150 }, maxWidth: '100%' })}
                >
                  Search
                </Button>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}></Stack>
                <Stack direction={{ default: 'column', sm: 'row' }} gap={2}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isExpired}
                          onChange={(event) => {
                            setIsExpired(event.target.checked)
                          }}
                        />
                      }
                      label="Archived"
                    />
                  </FormGroup>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      if (nameInputRef?.current?.value) {
                        nameInputRef.current.value = ''
                      }
                      setPage(1)
                      setReporterEmail('')
                    }}
                  >
                    Clear
                  </Button>
                </Stack>
              </Stack>
            </Box>

            {obstructionReportsResponse?.data && obstructionReportsResponse.data.length > 0 ? (
              <>
                <Menu
                  id="row-actions-menu"
                  anchorEl={rowActionsAnchorEl}
                  open={isActionsMenuOpen}
                  onClose={handleRowActionsClose}
                  MenuListProps={{
                    'aria-labelledby': `row-actions-button${selectedRow?.id}`,
                  }}
                >
                  <MenuItem href={`/obstruction-reports/${selectedRow?.id}`} component={Link}>
                    View
                  </MenuItem>
                  {hasWritePermissions && <MenuItem onClick={handleDeleteClick}>Archive</MenuItem>}
                </Menu>
                <DataTable
                  columns={columns}
                  data={obstructionReportsResponse.data}
                  total={obstructionReportsResponse.total}
                  paginationMode="server"
                  page={page}
                  pageSize={pageSize}
                  onPageChange={(newPage) => {
                    setPage(newPage)
                  }}
                  onPageSizeChange={(newPageSize) => {
                    setPageSize(newPageSize)
                  }}
                  orderBy={orderBy}
                  orderDirection={orderDirection}
                  onOrderChange={(newOrder) => {
                    setOrderBy(newOrder.field ? (newOrder.field as keyof ObstructionReport) : undefined)
                    setOrderDirection(newOrder.sort)
                  }}
                />
              </>
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography>No results found</Typography>
              </Box>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default ObstructionReportsSearch
